import { getHeaders, getUserDetails } from 'auth/auth';
import { createApi } from '@reduxjs/toolkit/query/react';
import VerifyDocument from 'pages/borrower-profile/documents/modals/verify';
import { baseQueryWithReauth } from '../baseQuery';

export const adminDocumentAPI = createApi({
  reducerPath: 'AdminDocument',
  tagTypes: ['updateAdminList', 'documentNotes'],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getDocumentList: builder.query({
      query: ({ applicationId, userId }) => {
        return {
          url: `/b2b/documents/aggregate/loan`,
          method: 'GET',
          params: {
            ...(applicationId ? { applicationId } : {}),
            ...(userId ? { borrowerId: userId } : {}),
          },
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => ({
        // Backend returns { success: true, data: [...grouped documents...] }
        // Data structure: [{ id, name, documentStatusId, sub_types: [{ name, documents: [...] }] }]
        data: response?.data ?? [],
        total: Array.isArray(response?.data) ? response.data.length : 0,
        skip: 0,
        limit: Array.isArray(response?.data) ? response.data.length : 0,
      }),
      providesTags: ['updateAdminList'],
    }),
    getUserProperty: builder.query({
      query: () => {
        return {
          url: `/b2b/documents/user_info`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    deleteDocument: builder.mutation({
      query: ({ documentId }) => {
        const headers = getHeaders();
        delete headers.accept;
        headers.Accept = 'application/json';
        return {
          url: `/b2b/documents/${documentId}`,
          method: 'DELETE',
          headers: headers,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    getDocumentById: builder.query({
      query: ({ documentId }) => ({
        url: `/b2b/documents/get`,
        method: 'GET',
        params: { document_id: documentId },
        headers: getHeaders(),
      }),
    }),
    uploadPredefinedDocs: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        const params = additionalParams || {};
        const normalizedParams = { ...params };

        if (
          normalizedParams.loan_application_id === undefined &&
          params.applicationId !== undefined
        ) {
          normalizedParams.loan_application_id = params.applicationId;
        }
        if (normalizedParams.borrower_id === undefined) {
          if (params.borrowerId !== undefined) {
            normalizedParams.borrower_id = params.borrowerId;
          } else if (params.userId !== undefined) {
            normalizedParams.borrower_id = params.userId;
          }
        }
        if (
          normalizedParams.document_category_id === undefined &&
          params.documentCategoryId !== undefined
        ) {
          normalizedParams.document_category_id = params.documentCategoryId;
        }
        if (normalizedParams.document_type_id === undefined) {
          if (params.documentTypeId !== undefined) {
            normalizedParams.document_type_id = params.documentTypeId;
          } else if (params.documentSubTypeId !== undefined) {
            normalizedParams.document_type_id = params.documentSubTypeId;
          }
        }
        if (normalizedParams.title === undefined && params.documentTitle) {
          normalizedParams.title = params.documentTitle;
        }
        if (
          normalizedParams.document_type_code === undefined &&
          params.documentTypeCode !== undefined
        ) {
          normalizedParams.document_type_code = params.documentTypeCode;
        }

        const appendedKeys = new Set();
        const appendIfDefined = (key, value) => {
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value);
            appendedKeys.add(key);
          }
        };

        appendIfDefined('loan_application_id', normalizedParams.loan_application_id);
        appendIfDefined('borrower_id', normalizedParams.borrower_id);
        appendIfDefined('document_category_id', normalizedParams.document_category_id);
        appendIfDefined('document_type_id', normalizedParams.document_type_id);
        appendIfDefined('title', normalizedParams.title);

        Object.entries(normalizedParams).forEach(([key, value]) => {
          if (!appendedKeys.has(key)) {
            appendIfDefined(key, value);
          }
        });
        return {
          url: `/b2b/documents/upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    updatePredefinedDocs: builder.mutation({
      query: ({ data, additionalParams, documentId }) => {
        const formData = new FormData();
        formData.append('file', data);
        Object.keys(additionalParams).forEach((key) => {
          formData.append(key, additionalParams[key]);
        });
        return {
          url: `/b2b/documents/update_document_file/${documentId}`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    verifyDocument: builder.mutation({
      query: ({ documentId }) => {
        return {
          url: `/b2b/documents/${documentId}/approve`,
          method: 'POST',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    rejectDocument: builder.mutation({
      query: ({ documentId, reason }) => {
        return {
          url: `/b2b/documents/${documentId}/reject`,
          method: 'POST',
          headers: getHeaders(),
          body: { reason },
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    renameDocument: builder.mutation({
      query: ({ documentId, title, isConsumer }) => ({
        url: isConsumer ? `/b2c/documents/update` : `/b2b/documents/update`,
        method: 'PUT',
        headers: getHeaders(),
        body: { document_id: documentId, title },
      }),
      invalidatesTags: ['updateAdminList'],
    }),
    getDocumentTypes: builder.query({
      query: () => ({
        url: `/b2b/documents/types`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) =>
        Array.isArray(response) ? response : response?.data ?? [],
    }),
    requestDocuments: builder.mutation({
      query: ({ loanApplicationId, borrowerId, dueDate, documentsRequested }) => ({
        url: `/b2b/documents/request`,
        method: 'POST',
        headers: getHeaders(),
        body: {
          loan_application_id: loanApplicationId,
          ...(borrowerId ? { borrower_id: borrowerId } : {}),
          ...(dueDate ? { due_date: dueDate } : {}),
          documents_requested: documentsRequested,
        },
      }),
      invalidatesTags: ['updateAdminList'],
    }),
    getDocumentNote: builder.query({
      query: ({ loanApplicationId, documentTypeId, isConsumer }) => ({
        url: isConsumer
          ? `/b2c/documents/loan/${loanApplicationId}/types/${documentTypeId}/notes`
          : `/b2b/documents/loan/${loanApplicationId}/types/${documentTypeId}/notes`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    updateDocumentNote: builder.mutation({
      query: ({ loanApplicationId, documentTypeId, notes, isConsumer }) => ({
        url: isConsumer
          ? `/b2c/documents/loan/${loanApplicationId}/types/${documentTypeId}/notes`
          : `/b2b/documents/loan/${loanApplicationId}/types/${documentTypeId}/notes`,
        method: 'PUT',
        headers: getHeaders(),
        body: { notes: notes ?? null },
      }),
    }),
    // --- Per-document chat-style notes (replaces the legacy single-string
    //     note above; legacy hooks stay for one release as a fallback). ---
    listDocumentNotes: builder.query({
      query: ({ documentId }) => ({
        url: `/b2b/documents/${documentId}/notes`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (_r, _e, { documentId }) => [
        { type: 'documentNotes', id: documentId },
      ],
    }),
    appendDocumentNote: builder.mutation({
      query: ({ documentId, body }) => ({
        url: `/b2b/documents/${documentId}/notes`,
        method: 'POST',
        headers: getHeaders(),
        body, // { note_text, addressed_to_user_id?, addressed_to_borrower_id? }
      }),
      invalidatesTags: (_r, _e, { documentId }) => [
        { type: 'documentNotes', id: documentId },
      ],
    }),
    markDocumentNoteRead: builder.mutation({
      query: ({ documentId, noteId }) => ({
        url: `/b2b/documents/${documentId}/notes/${noteId}/read`,
        method: 'PATCH',
        headers: getHeaders(),
      }),
      invalidatesTags: (_r, _e, { documentId }) => [
        { type: 'documentNotes', id: documentId },
      ],
    }),
    deleteDocumentNote: builder.mutation({
      query: ({ documentId, noteId }) => ({
        url: `/b2b/documents/${documentId}/notes/${noteId}`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      invalidatesTags: (_r, _e, { documentId }) => [
        { type: 'documentNotes', id: documentId },
      ],
    }),
  }),
});

// api for view document is present in hooks > pdfViewer.js

export const {
  useGetDocumentListQuery,
  useGetDocumentByIdQuery,
  useLazyGetDocumentByIdQuery,
  useUploadPredefinedDocsMutation,
  useUpdatePredefinedDocsMutation,
  useGetUserPropertyQuery,
  useLazyGetUserPropertyQuery,
  useDeleteDocumentMutation,
  useVerifyDocumentMutation,
  useRejectDocumentMutation,
  useRenameDocumentMutation,
  useGetDocumentTypesQuery,
  useRequestDocumentsMutation,
  useGetDocumentNoteQuery,
  useLazyGetDocumentNoteQuery,
  useUpdateDocumentNoteMutation,
  useListDocumentNotesQuery,
  useAppendDocumentNoteMutation,
  useMarkDocumentNoteReadMutation,
  useDeleteDocumentNoteMutation,
} = adminDocumentAPI;
